/**
 * `ACC-03` — sign-in against the real Cognito pool.
 *
 * SRP, via `amazon-cognito-identity-js`. The app client allows exactly
 * `ALLOW_USER_SRP_AUTH` and `ALLOW_REFRESH_TOKEN_AUTH`, so the password is never
 * sent anywhere — not even to Cognito — and `USER_PASSWORD_AUTH` is not an
 * option this code could fall back to even if it wanted one.
 *
 * **There is no hosted UI.** A pool with a hosted-UI domain refuses PrivateLink
 * for the whole pool, and the API reaches Cognito through a VPC endpoint because
 * there is no NAT gateway (ADR 0006 decision 3). So sign-in is a branded screen
 * in this app talking to Cognito's public API directly — which never touches
 * that VPC — and DESIGN.md wanted branded screens anyway.
 *
 * **The session comes from the ID token, not from a profile call.** ADR 0006
 * decision 10 makes `custom:org_id`, `custom:user_id` and `custom:role` Cognito
 * attributes that ride in the ID token, which is the same token API Gateway's
 * authorizer validates. Reading them here means the client and the server agree
 * about who the caller is by construction rather than by convention.
 */

import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'

import type { PublicApi } from '../api/public'
import { isThemePreference, type ThemePreference } from '../theme'
import {
  authorizeUrl,
  callbackErrorMessage,
  createPkce,
  exchangeCode,
  randomToken,
  rememberPending,
  shouldRetryAfterLinking,
  takePending,
  type CognitoDomainConfig,
  type SignInRoute
} from './federated'
import {
  AuthError,
  isRole,
  type AuthClient,
  type FederatedOutcome,
  type FinishAccountInput,
  type Session,
  type SignInInput,
  type SignUpInput,
  type SignUpOutcome
} from './contract'

export interface CognitoConfig {
  userPoolId: string
  clientId: string
  /**
   * The Cognito domain federated providers redirect through (ADR 0012), or
   * undefined when there is none yet — federated sign-in is then off.
   */
  domain?: string | undefined
  /**
   * The staff pool (ADR 0016): Match Point's Entra tenant and nothing else, or
   * undefined when this build has none — then there is no staff sign-in.
   */
  staff?: StaffPoolConfig | undefined
}

export interface StaffPoolConfig {
  userPoolId: string
  clientId: string
  /** The staff pool's Cognito domain, without a scheme. */
  domain: string
}

/**
 * What this client needs from the API side, injected rather than fetched.
 *
 * `publicApi` is the unauthenticated seam — signup runs before any session
 * exists, so it cannot ride the authenticated `ApiClient`. `getOrgName` is
 * resolved through the authenticated client, lazily: auth is constructed
 * before the api client (the api client's token getter needs auth), and the
 * first call that needs an org name happens in `session.bootstrap()`, by which
 * time both exist. A thunk breaks the construction cycle without a container.
 */
export interface CognitoApiBridge {
  publicApi: PublicApi
  /** The org's display name via the seam, or `null` when it cannot be had. */
  getOrgName: (orgId: string) => Promise<string | null>
  /**
   * The unauthenticated seam on the staff API, for `POST /auth/staff-provision`.
   * Present exactly when `CognitoConfig.staff` is.
   */
  staffApi?: PublicApi | undefined
}

/**
 * The theme override has nowhere on the server to live yet.
 *
 * `DESIGN.md` §5 and the seam's own contract say it is per account, so it should
 * follow the user to another machine. No endpoint stores it — `users` has no
 * such column and nothing in the contract exposes one — so it is kept in
 * `localStorage`, keyed by user id so two accounts on one browser do not
 * collide. That is a deliberate downgrade of a stated requirement, not an
 * oversight, and it reverts to the real behavior the moment an endpoint exists.
 */
const themeKey = (userId: string): string => `match-point.theme.${userId}`

/**
 * Unrecognized values fall back to `'system'`, which is what makes renaming a
 * theme survivable: the 2026-08-14 rename from `warm-clinical` to `ink-clinical`
 * left stored values that no longer name a theme, and every one of them resolves
 * to the OS preference instead of to a missing stylesheet.
 *
 * This asks `isThemePreference` rather than restating the union inline. The
 * inline form was a second copy of `THEME_PREFERENCES` that the type checker
 * could not connect to the first, so the rename updated one and would have left
 * the other stale without a word.
 */
function readTheme (userId: string): ThemePreference {
  const stored = globalThis.localStorage?.getItem(themeKey(userId))
  return isThemePreference(stored) ? stored : 'system'
}

/**
 * Build the session from the ID token's claims.
 *
 * Fails closed. `RequestPrincipalReader` on the server refuses a token missing
 * any of the three, so a client that rendered a half-session would show a
 * signed-in shell whose every request 401s — which reads as the API being
 * broken rather than the token being wrong.
 */
function sessionFromToken (session: CognitoUserSession, orgName: string): Session {
  const claims = session.getIdToken().decodePayload() as Record<string, unknown>

  const userId = claims['custom:user_id']
  const orgId = claims['custom:org_id']
  const role = claims['custom:role']
  const email = claims['email']

  if (typeof userId !== 'string' || typeof orgId !== 'string' || typeof email !== 'string') {
    throw new AuthError(
      'This account is not fully provisioned. Sign-in succeeded but the token is missing its identifiers.',
      'unavailable'
    )
  }

  if (!isRole(role)) {
    throw new AuthError('This account has no valid role.', 'unavailable')
  }

  return {
    userId,
    email,
    // `users` has no display-name column and the token carries none, so the
    // local part of the address stands in. Better than showing a uuid, and
    // honest about what is actually known.
    displayName: email.split('@')[0] ?? email,
    orgId,
    orgName,
    role,
    themePreference: readTheme(userId)
  }
}

/**
 * `ConfirmForgotPassword`'s refusals, in words a provider can act on.
 *
 * An unknown address does not get its own message. With
 * `prevent_user_existence_errors` enabled the pool answers it as a code
 * mismatch anyway, and a distinct message here would reopen the oracle the
 * request step keeps shut.
 */
function passwordResetError (code: string): AuthError {
  switch (code) {
    case 'CodeMismatchException':
    case 'ExpiredCodeException':
      return new AuthError('That code is not valid or has expired. Send a new one and try again.', 'invalid-credentials')
    case 'InvalidPasswordException':
      return new AuthError('That password does not meet the rules above.', 'validation')
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return new AuthError('Too many attempts. Wait a few minutes and try again.', 'unavailable')
    default:
      return new AuthError('The password was not changed.', 'unavailable')
  }
}

export function createCognitoAuthClient (config: CognitoConfig, bridge: CognitoApiBridge): AuthClient {
  const pool = new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId
  })

  // ADR 0016: a second pool, signed in to only through Entra. Its session is kept
  // under its own client id, so the two never overwrite each other in storage.
  const staffPool = config.staff
    ? new CognitoUserPool({ UserPoolId: config.staff.userPoolId, ClientId: config.staff.clientId })
    : null

  const userFor = (email: string): CognitoUser =>
    new CognitoUser({ Username: email, Pool: pool })

  /**
   * One session per browser. Signing in to one pool signs this browser out of the
   * other, so a staff member who also holds a test customer account never has two
   * tokens and a question about which one a request carries.
   */
  const leaveOtherPool = (target: CognitoUserPool): void => {
    for (const other of [pool, staffPool]) {
      if (other && other !== target) other.getCurrentUser()?.signOut()
    }
  }

  const sessionIn = async (from: CognitoUserPool): Promise<{ user: CognitoUser, session: CognitoUserSession } | null> => {
    const user = from.getCurrentUser()
    if (!user) return null

    return await new Promise((resolve) => {
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        // getSession silently exchanges the refresh token when the ID token has
        // expired, which is what makes the session survive a reload — ACC-03's
        // "the session survives refresh".
        resolve(error !== null || session === null || !session.isValid() ? null : { user, session })
      })
    })
  }

  /** The current session, refreshed if the ID token has expired: the staff pool's first. */
  const currentSession = async (): Promise<{ user: CognitoUser, session: CognitoUserSession } | null> =>
    (staffPool ? await sessionIn(staffPool) : null) ?? await sessionIn(pool)

  /**
   * The org's display name, which the token does not carry.
   *
   * A failure here must not fail sign-in: the session is valid without it and
   * the name is chrome. Falling back to an empty string would render a blank
   * heading, so the org id's prefix stands in — visibly provisional rather than
   * invisibly missing.
   */
  const orgNameFor = async (orgId: string): Promise<string> => {
    try {
      return (await bridge.getOrgName(orgId)) ?? `Org ${orgId.slice(0, 8)}`
    } catch {
      return `Org ${orgId.slice(0, 8)}`
    }
  }

  /**
   * Where a route's provider sends people back: this app's own callback route, on
   * the pool that route belongs to — the staff pool for `staff`, the customer pool
   * for the rest.
   */
  const domainConfig = (route: SignInRoute): CognitoDomainConfig | null => {
    const redirectUri = `${globalThis.location.origin}/auth/callback`
    if (route === 'staff') {
      return config.staff ? { domain: config.staff.domain, clientId: config.staff.clientId, redirectUri } : null
    }
    return config.domain ? { domain: config.domain, clientId: config.clientId, redirectUri } : null
  }

  /**
   * Stores tokens exactly where SRP sign-in stores them, so restore, refresh and
   * sign-out need no second path. `setSignInUserSession` writes the cache under
   * the Cognito username, which for a federated user is `Google_…`, not the email.
   */
  /** PKCE and state for one attempt, remembered in this tab, then off to the provider. */
  const leaveForProvider = async (provider: SignInRoute, returnTo: string | null, afterLinking: boolean): Promise<void> => {
    const domain = domainConfig(provider)
    if (!domain) throw new AuthError('Federated sign-in is not configured.', 'unavailable')

    const { verifier, challenge } = await createPkce()
    const state = randomToken(24)
    rememberPending({ state, verifier, provider, returnTo, afterLinking })
    globalThis.location.assign(authorizeUrl(domain, provider, state, challenge))
  }

  const adopt = (
    tokens: { idToken: string, accessToken: string, refreshToken: string },
    into: CognitoUserPool
  ): { user: CognitoUser, session: CognitoUserSession } => {
    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken })
    })
    const username = session.getIdToken().decodePayload()['cognito:username']
    if (typeof username !== 'string') {
      throw new AuthError('Sign-in returned a token with no username.', 'unavailable')
    }
    leaveOtherPool(into)
    const user = new CognitoUser({ Username: username, Pool: into })
    user.setSignInUserSession(session)
    return { user, session }
  }

  /** Whether a session's token carries an account, or only an identity. */
  const hasAccount = (session: CognitoUserSession): boolean =>
    typeof session.getIdToken().decodePayload()['custom:org_id'] === 'string'

  /** A fresh token from the refresh token, after claims were written to Cognito. */
  const refreshed = async (user: CognitoUser, session: CognitoUserSession, failure: string): Promise<CognitoUserSession> =>
    await new Promise<CognitoUserSession>((resolve, reject) => {
      user.refreshSession(session.getRefreshToken(), (error: Error | null, next: CognitoUserSession | null) => {
        if (error || !next) {
          reject(new AuthError(failure, 'unavailable'))
        } else {
          resolve(next)
        }
      })
    })

  const hydrate = async (session: CognitoUserSession): Promise<Session> => {
    const orgId = session.getIdToken().decodePayload()['custom:org_id']
    const orgName = typeof orgId === 'string' ? await orgNameFor(orgId) : ''
    return sessionFromToken(session, orgName)
  }

  /**
   * The staff half of the callback (ADR 0016). Entra has already decided this
   * person is staff — it issues a token only to people assigned to the app — so a
   * first sign-in provisions the account here, without a finish-account step, and
   * the refreshed token carries the claims.
   */
  const completeStaffSignIn = async (
    tokens: { idToken: string, accessToken: string, refreshToken: string },
    into: CognitoUserPool,
    returnTo: string | null
  ): Promise<FederatedOutcome> => {
    const { user, session } = adopt(tokens, into)

    if (hasAccount(session)) {
      return { kind: 'signed-in', session: await hydrate(session), returnTo }
    }

    if (!bridge.staffApi) {
      user.signOut()
      throw new AuthError('Staff sign-in is not configured.', 'unavailable')
    }

    try {
      await bridge.staffApi.staffProvision(session.getIdToken().getJwtToken())
    } catch (error) {
      // Not left half signed in with a token every staff route refuses.
      user.signOut()
      throw error
    }

    let next: CognitoUserSession
    try {
      next = await refreshed(user, session, 'Your staff account was set up. Sign in again to continue.')
    } catch (error) {
      // The same reason as above: a claimless token kept here would restore as a
      // staff session that every request refuses. Signing in again finishes it.
      user.signOut()
      throw error
    }
    return { kind: 'signed-in', session: await hydrate(next), returnTo }
  }

  return {
    restore: async () => {
      const current = await currentSession()
      // A federated identity with no account yet is not a session: nothing but
      // the finish-account screen can use its token, and a shell raised around it
      // would 401 on every request. Reading it as signed out sends them back
      // through sign-in, which lands on that screen again.
      if (!current || !hasAccount(current.session)) return null
      return await hydrate(current.session)
    },

    getIdToken: async () => {
      const current = await currentSession()
      // currentSession() refreshes an expired ID token before answering, so
      // what leaves here is always the token API Gateway will accept — or
      // nothing, when signed out, and unauthenticated routes ignore it.
      return current?.session.getIdToken().getJwtToken()
    },

    signIn: async ({ email, password }: SignInInput) => {
      leaveOtherPool(pool)
      const user = userFor(email)
      const session = await new Promise<CognitoUserSession>((resolve, reject) => {
        user.authenticateUser(
          new AuthenticationDetails({ Username: email, Password: password }),
          {
            onSuccess: resolve,
            onFailure: (error: { code?: string, message?: string }) => {
              // prevent_user_existence_errors is ENABLED on the app client, so
              // Cognito already refuses to distinguish a wrong password from an
              // unknown address. Mapping both to one message keeps this side
              // from reintroducing the oracle the pool just closed.
              const code = error.code ?? ''
              reject(
                code === 'NotAuthorizedException' || code === 'UserNotFoundException'
                  ? new AuthError('That email and password do not match.', 'invalid-credentials')
                  : code === 'UserNotConfirmedException'
                    ? new AuthError(
                      'This account is not confirmed yet. Enter the code we emailed you.',
                      'not-confirmed'
                    )
                    : new AuthError(error.message ?? 'Sign-in is unavailable.', 'unavailable')
              )
            }
          }
        )
      })

      return await hydrate(session)
    },

    federatedSignIn: config.domain
      ? async (provider, returnTo) => { await leaveForProvider(provider, returnTo, false) }
      : null,

    staffSignIn: staffPool
      ? async (returnTo) => { await leaveForProvider('staff', returnTo, false) }
      : null,

    isStaffSession: () => staffPool?.getCurrentUser() != null,

    completeFederatedSignIn: async (query): Promise<FederatedOutcome> => {
      if (query.error) {
        const failed = takePending(query.state ?? null)

        // ADR 0012 decision 5: the first sign-in after the trigger links an
        // existing account bounces, and the next one succeeds. Made once, by
        // this tab, for the attempt it started -- a second bounce is an error.
        if (shouldRetryAfterLinking(failed, query.error_description ?? null)) {
          await leaveForProvider(failed.provider, failed.returnTo, true)
          return { kind: 'redirecting' }
        }

        throw new AuthError(callbackErrorMessage(query.error, query.error_description ?? null), 'invalid-credentials')
      }

      // Refused unless this tab started the attempt: a code with a state we did
      // not issue is somebody else's sign-in, and completing it here would put
      // their account in this browser.
      const pending = takePending(query.state ?? null)
      if (!pending || !query.code) {
        throw new AuthError('That sign-in link has expired or was not started here. Try again.', 'invalid-credentials')
      }

      // The pool the attempt started on, which is the only one that can redeem its code.
      const domain = domainConfig(pending.provider)
      if (!domain) throw new AuthError('Federated sign-in is not configured.', 'unavailable')

      let tokens
      try {
        tokens = await exchangeCode(domain, query.code, pending.verifier)
      } catch {
        throw new AuthError('Sign-in did not complete. Try again.', 'unavailable')
      }

      if (pending.provider === 'staff' && staffPool) {
        return await completeStaffSignIn(tokens, staffPool, pending.returnTo)
      }

      const { session } = adopt(tokens, pool)

      if (!hasAccount(session)) {
        const email = session.getIdToken().decodePayload()['email']
        return { kind: 'needs-account', email: typeof email === 'string' ? email : '', returnTo: pending.returnTo }
      }

      return { kind: 'signed-in', session: await hydrate(session), returnTo: pending.returnTo }
    },

    finishFederatedAccount: async (input: FinishAccountInput): Promise<Session> => {
      const current = await currentSession()
      if (!current) throw new AuthError('Your sign-in has expired. Sign in again.', 'invalid-credentials')

      await bridge.publicApi.federatedSignUp(current.session.getIdToken().getJwtToken(), input)

      // The claims were written after this token was issued. A refresh asks
      // Cognito for a new one, which carries them.
      return await hydrate(await refreshed(current.user, current.session, 'Your account was created. Sign in again to continue.'))
    },

    /**
     * Signup goes to the API through the unauthenticated seam — see
     * `lib/api/public.ts` for why it is the API and not `CognitoUserPool.signUp`.
     */
    signUp: async (input: SignUpInput): Promise<SignUpOutcome> =>
      await bridge.publicApi.signUp({
        email: input.email,
        password: input.password,
        displayName: input.displayName,
        orgName: input.orgName,
        role: input.role
      }),

    confirmSignUp: async (email: string, code: string) => {
      await new Promise<void>((resolve, reject) => {
        userFor(email).confirmRegistration(code, true, (error: Error | null) => {
          if (error) {
            reject(new AuthError('That confirmation code is not valid or has expired.', 'invalid-credentials'))
          } else {
            resolve()
          }
        })
      })
    },

    resendConfirmationCode: async (email: string) => {
      await new Promise<void>((resolve, reject) => {
        // NodeCallback<E, T> is `(err?: E, result?: T) => void` — the error is
        // optional rather than nullable, and there is a result nobody needs.
        userFor(email).resendConfirmationCode((error?: Error) => {
          if (error) {
            // Deliberately vague, and for the same reason ACC-04's reset is:
            // a distinct "no such account" here would be an enumeration oracle
            // over a list of behavioral-health providers.
            reject(new AuthError('We could not send another code just now.', 'unavailable'))
          } else {
            resolve()
          }
        })
      })
    },

    requestPasswordReset: async (email: string) => {
      await new Promise<void>((resolve) => {
        // Resolves either way. ACC-04 and the contract both require that this
        // never confirms whether an address is registered, which is the
        // deliberate opposite of signup's 409.
        userFor(email).forgotPassword({
          onSuccess: () => resolve(),
          onFailure: () => resolve()
        })
      })
    },

    confirmPasswordReset: async (email: string, code: string, newPassword: string) => {
      await new Promise<void>((resolve, reject) => {
        userFor(email).confirmPassword(code, newPassword, {
          onSuccess: () => resolve(),
          onFailure: (error: { code?: string, name?: string }) => {
            reject(passwordResetError(error.code ?? error.name ?? ''))
          }
        })
      })
    },

    signOut: async () => {
      // Both pools: whichever this browser holds a session in. globalSignOut would
      // revoke every device's refresh token; signOut ends this browser's session
      // only, which is what a sign-out button means.
      for (const from of [staffPool, pool]) {
        const user = from?.getCurrentUser()
        if (user) await new Promise<void>((resolve) => { user.signOut(() => resolve()) })
      }
    },

    setThemePreference: async (preference: ThemePreference) => {
      const current = await currentSession()
      if (!current) throw new AuthError('Not signed in.', 'unavailable')

      const session = await hydrate(current.session)
      globalThis.localStorage?.setItem(themeKey(session.userId), preference)
      return { ...session, themePreference: preference }
    },

    // Null in production, and the contract says so: re-issuing a session as
    // another role is something only the fake can do.
    impersonateRole: null
  }
}

